<script setup lang="ts">
import {ref, computed, inject, watch} from 'vue'
import { useStore } from 'vuex'
import { useAuth } from '@/composables/useAuth'
import router from '@/router'
import { useRoute } from 'vue-router'
import { DarkModeProvider, darkModeProviderKey } from '@/utils/theme.util'
import { aboutEventBus, preferencesEventBus, closeAllModalsEventBus } from '@/utils/eventBus'

import AuthModal from '@/components/login/AuthModal.vue'
import ProfileModal from '@/components/profile/ProfileModal.vue'
import UserPreferenceModal from '@/components/preferences/UserPreferenceModal.vue'
import { Button } from '@/components/ui/button'

import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip'

import {
  IconLogOut,
  IconSun,
  IconMoon,
  IconEllipsisVertical,
  IconSlidersHorizontal,
  IconUser,
  IconSettings,
  IconInfo
} from '@/components/icons/lucide-vue'

import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '@/store/modules/auth'
import { useNavStore } from '@/store/modules/nav'
import { useUsersStore } from '@/store/modules/user'
import { useToast } from 'primevue/usetoast'
import {createShareService} from "@/services/share.service";
import AboutModal from "@/components/app/about/AboutModal.vue";
import type { LogoData } from '@/types/client'

const store = useStore()
const usersStore = useUsersStore()
const authStore = useAuthStore()
const showAboutModal = ref(false)

const route = useRoute()
const { user } = storeToRefs(usersStore)
const isLoggedIn = computed(() => !!user.value)
const { isDark, updateMode } = inject<DarkModeProvider>(
  darkModeProviderKey
) as DarkModeProvider

const toast = useToast()


const isAuthDialogOpen = ref(false)
const isProfileOpen = ref(false)
const isPreferencesOpen = ref(false)

aboutEventBus.on(() => {
  showAboutModal.value = true
})

const requestedTab = ref<'preferences' | 'display' | undefined>(undefined)
const scrollToTarget = ref<string | undefined>(undefined)

preferencesEventBus.on((payload) => {
  requestedTab.value = payload?.tab
  scrollToTarget.value = payload?.scrollTo
  isPreferencesOpen.value = true
})

closeAllModalsEventBus.on(() => {
  isAuthDialogOpen.value = false
  isProfileOpen.value = false
  isPreferencesOpen.value = false
  showAboutModal.value = false
})

const shareService = createShareService(store)

const logoData = ref<LogoData | null>(shareService.getLogoData())

watch(
    () => route.path,
    () => {
      updateLogo()
    },
    { immediate: true }
)

function updateLogo() {
  const path = route.path
  const isBaseBet = /^\/bet\/?$/.test(path)
  const isBaseOdds = /^\/odds\/?$/.test(path)
  
  let brandName = 'login'
  if( isBaseOdds ) {
    brandName = 'askodds'
  } else if( isBaseBet ) {
    brandName = 'askbookmaker'
  }

  logoData.value = shareService.getLogoData({brand: brandName })
}


async function openProfileDialog() {
  try {
    await authStore.checkLogin()

    if (isLoggedIn.value) {
      isProfileOpen.value = true
    } else {
      isAuthDialogOpen.value = true
    }
  } catch (err) {
    isAuthDialogOpen.value = true
  }
}

function goToPreferences() {
  isPreferencesOpen.value = true
}
</script>

<template>
  <!-- Desktop & tablet view -->
  <div class="hidden md:flex space-x-2 items-center">
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger as-child>
          <Button @click="openProfileDialog" class="transition-all duration-300 active:scale-95">
            <IconSettings v-if="isLoggedIn" />
            <IconUser v-else />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{{ isLoggedIn ? 'Profile' : 'Login' }}</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger as-child>
          <Button @click="goToPreferences">
            <IconSlidersHorizontal />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Preferences</TooltipContent>
      </Tooltip>



      <Tooltip v-if="isLoggedIn">
        <TooltipTrigger as-child>
          <Button @click="authStore.logout" class="transition-all duration-300 active:scale-95">
            <IconLogOut />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Logout</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  </div>

  <!-- Mobile view -->
  <div class="flex md:hidden items-center space-x-2">
    <Button @click="openProfileDialog" size="icon" class="rounded-full transition-all duration-300 active:scale-95">
      <IconSettings v-if="isLoggedIn" />
      <IconUser v-else />
    </Button>

    <DropdownMenu>
      <DropdownMenuTrigger as-child>
        <Button size="icon" class="rounded-full" variant="ghost">
          <IconEllipsisVertical />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent class="w-48">


        <DropdownMenuItem @click="goToPreferences">
          <IconSlidersHorizontal class="mr-2" /> Preferences
        </DropdownMenuItem>

        <DropdownMenuSeparator v-if="isLoggedIn" />

        <DropdownMenuItem v-if="isLoggedIn" @click="authStore.logout">
          <IconLogOut class="mr-2" /> Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>


  <AuthModal v-model:visible="isAuthDialogOpen" />
  <ProfileModal v-model:visible="isProfileOpen" />
  <UserPreferenceModal 
    v-model:visible="isPreferencesOpen" 
    :initial-tab="requestedTab" 
    :scroll-to="scrollToTarget"
    @update:visible="val => !val && (requestedTab = undefined, scrollToTarget = undefined)" 
  />
  <AboutModal v-model:is-open="showAboutModal" />
</template>